// Forecast parameters component

import React from 'react';
import { ChartBarIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { SimulationMetadata } from '@/types/forecast';

interface ForecastParametersProps {
  sourceAnalysis: WeibullAnalysisResult | null;
  simulationMetadata?: SimulationMetadata;
}

export const ForecastParameters: React.FC<ForecastParametersProps> = ({
  sourceAnalysis,
  simulationMetadata
}) => {
  
  if (!sourceAnalysis) {
    return (
      <div className="space-y-4">
        {/* Weibull Parameters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center space-x-2 mb-3">
            <ChartBarIcon className="w-5 h-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900">Weibull Parameters</h3>
          </div>
          <div className="text-center py-6">
            <div className="text-gray-400 mb-3">
              <ChartBarIcon className="w-12 h-12 mx-auto" />
            </div>
            <p className="text-sm text-gray-600">
              Load an analysis to see Weibull parameters
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { parameters } = sourceAnalysis;

  return (
    <div className="space-y-4">
      {/* Simulation Status */}
      {simulationMetadata && (simulationMetadata.method === 'monte-carlo' || simulationMetadata.method === 'parametric-bootstrap') && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center space-x-2 mb-4">
            <ClockIcon className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">Simulation Status</h3>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-600">Simulations Run</span>
                <span className="text-lg font-semibold text-gray-900">
                  {simulationMetadata.totalRuns?.toLocaleString() || 'N/A'}
                </span>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-600">Convergence</span>
                <div className="flex items-center space-x-1">
                  {simulationMetadata.convergenceAchieved ? (
                    <>
                      <CheckCircleIcon className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-semibold text-green-700">Achieved</span>
                    </>
                  ) : (
                    <>
                      <ClockIcon className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm font-semibold text-yellow-700">Max Iterations</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {simulationMetadata.computationTimeMs && (
            <div className="mt-3 bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Computation Time</span>
                <span className="text-sm font-semibold text-gray-900">
                  {simulationMetadata.computationTimeMs < 1000 
                    ? `${simulationMetadata.computationTimeMs}ms`
                    : `${(simulationMetadata.computationTimeMs / 1000).toFixed(1)}s`
                  }
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Weibull Parameters Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center space-x-2 mb-4">
          <ChartBarIcon className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">Weibull Parameters</h3>
        </div>

      {/* Parameters */}
      <div className="space-y-4">
        
        {/* Shape Parameter (Beta) */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Shape Parameter (β)</span>
            <span className="text-lg font-semibold text-gray-900">
              {parameters.shape.toFixed(2)}
            </span>
          </div>
          <div className="text-xs text-gray-600">
            {parameters.shape < 1 ? 'Decreasing failure rate (early failures)' : 
             parameters.shape === 1 ? 'Constant failure rate (random failures)' :
             parameters.shape < 2 ? 'Slightly increasing failure rate' :
             parameters.shape < 3 ? 'Moderate wear-out pattern' :
             'Strong wear-out pattern'}
          </div>
          {parameters.shapeCI && (
            <div className="text-xs text-gray-500 mt-1">
              95% CI: [{parameters.shapeCI.lower.toFixed(2)}, {parameters.shapeCI.upper.toFixed(2)}]
            </div>
          )}
        </div>

        {/* Scale Parameter (Eta) */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Scale Parameter (η)</span>
            <span className="text-lg font-semibold text-gray-900">
              {parameters.scale.toFixed(1)} years
            </span>
          </div>
          <div className="text-xs text-gray-600">
            Characteristic life - 63.2% failure probability
          </div>
          {parameters.scaleCI && (
            <div className="text-xs text-gray-500 mt-1">
              95% CI: [{parameters.scaleCI.lower.toFixed(1)}, {parameters.scaleCI.upper.toFixed(1)}] years
            </div>
          )}
        </div>

        {/* Goodness of Fit */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Goodness of Fit (R²)</span>
            <span className="text-lg font-semibold text-gray-900">
              {(parameters.rSquared * 100).toFixed(1)}%
            </span>
          </div>
          <div className="text-xs text-gray-600">
            {parameters.rSquared > 0.95 ? 'Excellent fit' :
             parameters.rSquared > 0.90 ? 'Good fit' :
             parameters.rSquared > 0.80 ? 'Acceptable fit' :
             'Poor fit - use forecast with caution'}
          </div>
        </div>

        </div>
      </div>
    </div>
  );
};
